import {forwardRef, type MouseEvent, type ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {CircularLoader} from '../circular-loader';
import {HelpTooltip} from '../help-tooltip';
import {cn} from '../lib/cn';
import {SubSectionSize} from './constants';

type ReactEntity = ReactNode;

export type SectionProps = {
  children?: ReactEntity;
  title?: ReactEntity;
  titleTooltip?: ReactEntity | string;
  subtitle?: ReactEntity;
  subtitleTooltip?: ReactEntity;
  className?: string;
  titleClassName?: string;
  loading?: boolean;
  showLoader?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  linkContent?: ReactEntity;
  size?: SubSectionSize;
};

function TooltipInfo({content, className}: {content: ReactNode; className?: string}) {
  return (
    <HelpTooltip
      side="right"
      content={content}
      contentClassName="max-w-64"
      className={cn('text-[var(--section-tooltip-color)]', className)}
      onClick={(event: MouseEvent) => event.stopPropagation()}
      label={typeof content === 'string' ? content : 'More information'}
    />
  );
}

const Section = forwardRef<HTMLDivElement, SectionProps>(
  (
    {
      children,
      title,
      subtitle,
      subtitleTooltip,
      className,
      titleClassName,
      titleTooltip,
      loading,
      disabled,
      showLoader,
      linkContent,
      hidden,
      size,
    },
    ref,
  ) => {
    const {t} = useTranslation();

    return (
      <div
        ref={ref}
        className={cn(
          'mb-6 box-border flex w-full cursor-default flex-col gap-6 rounded-lg border border-solid border-[var(--chekin-color-gray-3)] bg-white px-6 py-10',
          loading && 'cursor-progress',
          disabled && 'pointer-events-none opacity-50',
          {'!hidden': hidden},
          className,
        )}
      >
        {(title || subtitle) && (
          <div className="flex flex-col gap-2">
            {title && (
              <div
                className={cn(
                  'flex max-w-[85%] items-center text-lg font-semibold text-[var(--section-title-color)] md:max-w-full',
                  size !== SubSectionSize.L && 'subsection-title',
                  titleClassName,
                )}
              >
                {title}
                {titleTooltip && (
                  <div className="ml-2.5">
                    <TooltipInfo content={titleTooltip} />
                  </div>
                )}
                {linkContent && (
                  <div className="ml-6 text-sm font-semibold text-[var(--section-link-color)] no-underline hover:opacity-70 active:opacity-100">
                    {linkContent}
                  </div>
                )}
              </div>
            )}
            {subtitle && (
              <div className="w-full max-w-[720px] md:max-w-full">
                <div className="inline text-base font-normal text-[var(--section-subtitle-color)]">
                  {subtitle}
                </div>
                {subtitleTooltip && (
                  <div className="ml-1.5 inline-block align-text-top">
                    <TooltipInfo content={subtitleTooltip} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {loading && showLoader ? (
          <CircularLoader
            size="md"
            label={t('loading')}
            className="mx-auto mb-3 mt-2.5"
          />
        ) : (
          children
        )}
      </div>
    );
  },
);
Section.displayName = 'Section';

const SubSection = forwardRef<HTMLDivElement, SectionProps>(
  ({className, ...props}, ref) => (
    <Section
      ref={ref}
      className={cn(
        'm-0 box-border rounded-none border-none bg-transparent p-0 [&_.subsection-title]:text-base [&_.subsection-title]:font-semibold',
        className,
      )}
      {...props}
    />
  ),
);
SubSection.displayName = 'SubSection';

const DividingSubsection = forwardRef<HTMLDivElement, SectionProps>(
  ({className, ...props}, ref) => (
    <SubSection
      ref={ref}
      className={cn('border-0 border-t border-solid border-t-[#f1f1f1] pt-6', className)}
      {...props}
    />
  ),
);
DividingSubsection.displayName = 'DividingSubsection';

export {DividingSubsection, Section, SubSection, SubSectionSize};
